<template>
  <Card height="full">
    <View gap="md" direction="column">
      <View align="center" gap="sm">
        <Icon :name="'file-check-02'" />
        <Text variant="h2">{{ reportTitle }}</Text>
      </View>

      <View v-if="isLoading" justify="center" align="center" class="pdf-section">
        <LoadingIndicator />
      </View>

      <View v-else-if="error" justify="center" align="center" class="error-message" direction="column" gap="md">
        <IconTile icon="file-x-02" color="neutral" size="large" shape="rounded" :colored-icon="true" />

        <div>
          <Text variant="h3" align="center">Could not load the report</Text>
          <Text variant="body-large" color="tertiary">Please reload the page or try again later</Text>
        </div>
      </View>

      <Card class="pdf-section" variant="outline" v-else-if="format === 'pdf'">
        <View direction="column" gap="sm">
          <Link v-if="report" :to="report" target="_blank" color="accent" class="open-in-tab">Open in a new tab</Link>
          <iframe v-if="report" :src="report" class="full-width pdf-frame" frameborder="0" title="Report" />
        </View>
      </Card>

      <Card class="pdf-section" variant="outline" v-else :style="{ opacity: iframeLoaded ? 1 : 0 }">
        <iframe
          v-if="report"
          ref="iframeRef"
          :srcdoc="report"
          class="full-width"
          frameborder="0"
          scrolling="no"
          @load="onIframeLoad"
        />
      </Card>
    </View>
  </Card>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, capitalize, computed } from 'vue';

import { Card, LoadingIndicator, View, Icon, Text, IconTile, Link } from '@tallo/design-system';

import { ReportFormat } from '../../enums/report-format.type';
import { ScreeningProductType } from '../../enums/screening-product-type';

const props = withDefaults(
  defineProps<{
    /** HTML document for the iframe, or an object URL of the PDF when `format` is 'pdf'. */
    report: string | undefined;
    type: ScreeningProductType;
    isLoading: boolean;
    error: string | undefined;
    format?: ReportFormat;
  }>(),
  { format: 'html' },
);

// Owners and renters see the criminal product as a background report; the enum keeps the vendors' name.
const reportTitle = computed(() =>
  props.type === ScreeningProductType.CRIMINAL ? 'Background report' : `${capitalize(props.type)} report`,
);

const iframeRef = ref<HTMLIFrameElement | null>(null);
const iframeLoaded = ref(false); // New ref to track iframe load state

function adjustIframeHeight() {
  if (iframeRef.value) {
    try {
      const iframe = iframeRef.value;
      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDocument) {
        const iframeHeight = iframeDocument.documentElement.scrollHeight || iframeDocument.body.scrollHeight;
        iframe.style.height = iframeHeight + 'px';
      }
    } catch (error) {
      console.error('Error adjusting iframe height:', error);
    }
  }
}

function injectCustomStyles() {
  if (iframeRef.value) {
    try {
      const iframe = iframeRef.value;
      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDocument) {
        const styleElement = iframeDocument.createElement('style');
        styleElement.type = 'text/css';
        styleElement.innerHTML = `
          .shareable\\.credit-site .shareable\\.credit-site-content {
            padding-top: 0 !important;
          }
        `;
        iframeDocument.head.appendChild(styleElement);
      }
    } catch (error) {
      console.error('Error injecting custom styles into iframe:', error);
    }
  }
}

function onIframeLoad() {
  adjustIframeHeight();
  injectCustomStyles();
  iframeLoaded.value = true;
}

watch(
  () => props.report,
  () => {
    iframeLoaded.value = false;
    adjustIframeHeight();
    injectCustomStyles();
  },
);

onMounted(() => {
  adjustIframeHeight();
  injectCustomStyles();
});
</script>

<style scoped>
.full-width {
  width: 100%;
  border: none;
}

.pdf-section,
.error-message {
  min-height: 65vh;
}

.pdf-frame {
  min-height: 80vh;
}

.open-in-tab {
  align-self: flex-end;
}
</style>
